import { type Stripe, type StripeElements } from "@stripe/stripe-js";
import { makeAutoObservable } from "mobx";

import { type AnalyticsManager } from "services/analytics/analyticsManager";
import { type SetupIntentStore } from "services/setupIntent/setupIntentStore";
import { type FormSubmitResult, FormValidationError } from "types/form";

type StripeFormState = "pending" | "loading" | "error" | StripeFormLoadedState;

export class StripeFormLoadedState {
  private _confirming = false;

  constructor(
    stripe: Stripe,
    clientSecret: string,
    private readonly _analyticsManager: AnalyticsManager,
  ) {
    this.stripe = stripe;
    this.clientSecret = clientSecret;
    makeAutoObservable(this);
  }

  readonly stripe: Stripe;

  readonly clientSecret: string;

  async confirm(
    elements: StripeElements,
    returnUrl: string,
    billingAddress?: { country: string; postalCode: string },
    inlineConfirm?: boolean,
  ): Promise<FormSubmitResult> {
    if (this._confirming) {
      return;
    }
    this._confirming = true;

    this._analyticsManager.event("stripe-submit");

    const confirmParams = {
      return_url: returnUrl,
      ...(billingAddress
        ? {
            payment_method_data: {
              billing_details: {
                address: {
                  country: billingAddress.country,
                  postal_code: billingAddress.postalCode,
                },
              },
            },
          }
        : {}),
    };

    try {
      const result = inlineConfirm
        ? await this.stripe.confirmSetup({ elements, confirmParams, redirect: "if_required" })
        : await this.stripe.confirmSetup({ elements, confirmParams });

      if (result.error) {
        if (
          result.error.code === "setup_intent_unexpected_state" &&
          result.error.setup_intent?.status === "succeeded"
        ) {
          return;
        }
        this._analyticsManager.event("stripe-submit-error", { error: result.error.message });
        return new FormValidationError(result.error.message || "Something went wrong");
      }
    } finally {
      this._confirming = false;
    }
  }
}

export class StripeFormStore {
  private _state: StripeFormState = "pending";

  constructor(
    private readonly _setupIntentStore: SetupIntentStore,
    private readonly _analyticsManager: AnalyticsManager,
  ) {
    makeAutoObservable(this, {
      _setupIntentStore: false,
      _analyticsManager: false,
    } as any);
  }

  get state(): StripeFormState {
    return this._state;
  }

  private set state(value: StripeFormState) {
    this._state = value;
  }

  async retry(): Promise<void> {
    this._state = "pending";
    await this._setupIntentStore.retry();
    this._applySetupIntentState();
  }

  async init(): Promise<void> {
    if (this._state !== "pending") {
      return;
    }

    this._analyticsManager.event("stripe-loading");
    this._state = "loading";

    await this._setupIntentStore.init();
    this._applySetupIntentState();
  }

  private _applySetupIntentState(): void {
    const stripe = this._setupIntentStore.stripe;
    const clientSecret = this._setupIntentStore.clientSecret;

    if (this._setupIntentStore.state === "ready" && stripe && clientSecret) {
      this.state = new StripeFormLoadedState(stripe, clientSecret, this._analyticsManager);
      return;
    }

    this._analyticsManager.event("stripe-error", { error: "setup-intent-failed" });
    this.state = "error";
  }
}
